<template>
  <div id="app" class="min-h-screen bg-base-200">
    <!-- Debug info -->
    <div v-if="false" class="bg-error/10 p-2 text-xs">
      Route: {{ route.name }} | Show Header/Footer: {{ showHeaderFooter }}
    </div>
    <!-- Dev Panel (development only; can be forced via VITE_SHOW_DEV_PANEL=true) -->
    <DevPanel v-if="showDev" />

    <!-- Authentication Loading Overlay -->
    <div
      v-if="authStore.isLoading && !authStore.hasCheckedSession"
      class="fixed inset-0 bg-base-100/90 flex items-center justify-center z-50"
    >
      <div class="text-center">
        <LoadingSpinner size="xl" color="primary" />
        <p class="mt-4 text-lg font-medium text-base-content">Authenticating...</p>
        <p class="mt-2 text-sm text-base-content/70">Please wait while we verify your session</p>
      </div>
    </div>

    <!-- Re-authentication Loading Overlay -->
    <div
      v-else-if="authStore.isLoading && authStore.hasCheckedSession"
      class="fixed inset-0 bg-neutral/50 flex items-center justify-center z-50"
    >
      <div class="bg-base-100 rounded-lg p-6 text-center shadow-xl">
        <LoadingSpinner size="lg" color="primary" />
        <p class="mt-4 text-lg font-medium text-base-content">Re-authenticating...</p>
        <p class="mt-2 text-sm text-base-content/70">Refreshing your session, please wait</p>
      </div>
    </div>

    <!-- Header - show on all pages except login -->
    <Header v-if="showHeaderFooter" />

    <!-- Desktop left drawer (authenticated pages only) -->
    <LeftDrawer v-if="showHeaderFooter && authStore.isAuthenticated" />

    <!-- Main content -->
    <main class="min-h-screen">
      <router-view />
    </main>

    <!-- Footer - show on all pages except login -->
    <Footer v-if="showHeaderFooter" />
  </div>
</template>

<script setup lang="ts">
  import { onMounted, computed } from 'vue'
  import { useRoute } from 'vue-router'
  import { useAuthStore } from './stores/auth'
  import Header from './components/Header.vue'
  import Footer from './components/Footer.vue'
  import LoadingSpinner from './components/LoadingSpinner.vue'
  import DevPanel from './dev/DevPanel.vue'
  import LeftDrawer from './components/LeftDrawer.vue'

  const route = useRoute()
  const authStore = useAuthStore()

  const showHeaderFooter = computed(() => {
    const name = String(route.name || '')
    console.log('Current route name:', name)
    const hideOn = new Set(['login', 'loginVerify', 'passwordReset'])
    return !hideOn.has(name)
  })

  const env = (import.meta as any).env || {}
  const isDev = !!env?.DEV
  const showDev = isDev || env?.VITE_SHOW_DEV_PANEL === 'true'

  onMounted(async () => {
    // Avoid auto re-auth on the login page
    if (route.name !== 'login') {
      await authStore.checkSession()
    }
  })
</script>

<style>
  /* Ensure the app takes full height */
  #app {
    min-height: 100vh;
  }

  /* Ensure main content takes available space */
  main {
    min-height: calc(100vh - 64px - 200px); /* Adjust for header and footer heights */
  }
</style>
